import React, { useState } from 'react';
import axios from 'axios';
import { Package, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LANDED_COST_RE = /freight|shipping|shipment|clearing|customs?|duty|duties|cargo|haulage|forwarder|logistics|courier|import/i;

/**
 * LandedCostPrompt - shown on review-needed bank transactions that look like
 * a landed cost (freight/clearing/duty). Lets the user attach it to a recent
 * inventory purchase in two clicks.
 */
export const LandedCostPrompt = ({ tx, currencySymbol = '₦', onDone }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [purchases, setPurchases] = useState(null);
  const [selected, setSelected] = useState(null);
  const [costName, setCostName] = useState('');

  if (tx.direction !== 'outflow' || !LANDED_COST_RE.test(tx.description || '')) return null;

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    setSelected(null);
    setCostName((tx.description || '').slice(0, 40));
    try {
      const r = await axios.get(`${API}/inventory/cost-attach-suggestion`, { params: { description: tx.description || '' } });
      setPurchases(r.data?.purchases || []);
    } catch { setPurchases([]); } finally { setLoading(false); }
  };

  const attach = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/inventory/attach-cost`, {
        bft_id: tx.id, source_id: selected.source_id, cost_name: costName || null,
      });
      toast.success(r.data?.message || 'Cost added to the purchase.');
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not attach the cost.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 border border-teal-200 text-[11px] text-teal-800 hover:bg-teal-100 transition-colors"
        data-testid={`landed-cost-prompt-${tx.id}`}
      >
        <Package className="w-3 h-3" />
        Looks like a cost of getting goods in: add to a stock purchase?
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="landed-cost-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Package className="w-4 h-4 text-teal-600" /> Add to a stock purchase</DialogTitle>
            <DialogDescription className="text-xs">
              {currencySymbol}{Math.abs(tx.amount || 0).toLocaleString()} will be added to the cost of the purchase you pick,
              and this bank transaction marked Completed. It hits your profit when the goods sell: nothing is double-counted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cost name</Label>
              <Input value={costName} onChange={(e) => setCostName(e.target.value)} className="h-8 text-sm mt-1" placeholder="e.g. Freight. Lagos port" data-testid="landed-cost-name-input" />
            </div>
            <div>
              <Label className="text-xs">Which purchase was this cost for?</Label>
              {loading ? (
                <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
              ) : (
                <div className="mt-1 max-h-[220px] overflow-y-auto space-y-1">
                  {(purchases || []).map(p => (
                    <button
                      key={p.source_id}
                      type="button"
                      disabled={!p.has_breakdown}
                      onClick={() => setSelected(p)}
                      className={`w-full text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${selected?.source_id === p.source_id ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:bg-slate-50'} ${!p.has_breakdown ? 'opacity-60 cursor-not-allowed' : ''}`}
                      data-testid={`landed-cost-purchase-${p.source_id}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span className="truncate font-medium">{p.description || 'Purchase'}</span>
                        <span className="whitespace-nowrap font-semibold">{currencySymbol}{Number(p.amount || 0).toLocaleString()}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {String(p.date || '').slice(0, 10)}
                        {!p.has_breakdown && ' · Provide its breakdown first: this transaction will appear in the "Paid separately" costs list there.'}
                      </p>
                    </button>
                  ))}
                  {purchases && purchases.length === 0 && (
                    <p className="text-xs text-slate-400 py-3 text-center">No recent inventory purchases found.</p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={!selected || saving} onClick={attach} data-testid="landed-cost-confirm-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add to purchase cost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
